import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Widgets } from "../createForm/Widgets";
import "./ApiSelector.css";
import {
  getMetaDataConfig,
  validateMetaDataResponse,
} from "../../services/uiConfigService";
import { getConfigFromClientConfig } from "../../services/bannerServices";
import { fetchClientConfig } from "../../services/manageHomeScreenService";
import { getLob } from "../../services/authenticationService";
import { validateWithOtp } from "../../stateManagement/actions/validateOtpPopupActions";
import { manageUpdateAccessObj } from "../manageUpdateAccess/manageUpdateAccessTypes";
import {
  ChannelkartNetworkGet,
  META_DATA_BATCH_API,
} from "../../services/networkService";
import { GenericPopUp } from "../../components/popup/genericPopUp";
import { metaDataBatchPayload, popupType } from "../../types/types";

interface SchemaItem {
  id: string;
  name: string;
  widgets: WidgetItem[];
}

interface WidgetItem {
  id: string;
  widgetType: string;
  config: Record<string, any>;
  mappedApiField?: string;
  extendedMappedApiFieldName?: string;
  name?: string;
  icon?: string;
  sectionId?: string;
}

interface FormConfig {
  sections?: SchemaItem[];
}

interface ApiSelectorProps {
  widgetPanelDisabled?: boolean;
  onSave?: (formSections: any, meta: any) => void;
  onFormChange?: (updatedSections: SchemaItem[], updatedMeta?: any) => void;
  formSections: SchemaItem[] | FormConfig;
  meta: {
    api: string;
    formName: string;
    buttonLabel: string;
    formId: string;
  };
}

const ApiSelector: React.FC<Partial<ApiSelectorProps>> = ({
  formSections,
  meta,
  widgetPanelDisabled = false,
  onSave,
  onFormChange,
}) => {
  const location = useLocation();
  
  const extractSections = (data: any): SchemaItem[] => {
    if (!data) return [];
    
    if (Array.isArray(data)) {
      return data;
    }
    
    if (data.sections && Array.isArray(data.sections)) {
      return data.sections;
    }
    
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        return extractSections(parsed);
      } catch (error) {
        console.error("Error parsing JSON:", error);
        return [];
      }
    }
    
    return [];
  };

  let initialJsonSchema: SchemaItem[] = [];

  const [apiConfig, setApiConfig] = useState<Record<string, string[]>>({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEntity = async () => {
      try {
        const response = await getMetaDataConfig("entityconfig", "entity");
        const apiConfigData: Record<string, string[]> | undefined =
          response?.domainValues?.[0];
        if (apiConfigData) {
          const apiConfig = Object.entries(apiConfigData).reduce(
            (acc, [key, value]) => {
              acc[key] = value;
              return acc;
            },
            {} as Record<string, string[]>
          );

          setApiConfig(apiConfig);
        }
      } catch (error) {
        console.error("Error fetching portal configuration:", error);
      }
    };

    fetchEntity();
  }, []);

  try {
    if (formSections) {
      initialJsonSchema = extractSections(formSections);
    } else if (location.state?.jsonSchema) {
      initialJsonSchema = extractSections(location.state.jsonSchema);
    }
  } catch (error) {
    console.error("Invalid JSON schema: ", error);
  }

  const [jsonSchema, setJsonSchema] = useState<SchemaItem[]>(initialJsonSchema);
  const [selectedApi, setSelectedApi] = useState<keyof typeof apiConfig>();
  const [hasManuallyChangedApi, setHasManuallyChangedApi] = useState(false);
  const [formName, setFormName] = useState<string>("");
  const [buttonLabel, setButtonLabel] = useState<string>("");
  const [formId, setFormId] = useState<string>("");
  const [popupMessage, setPopupMessage] = useState<string>("");
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [openGenericModal, setOpenGenericModal] = useState<boolean>(false);
  const [usedApiFields, setUsedApiFields] = useState<Record<string, string>>({});
  const [customMappedApiFieldInputs, setCustomMappedApiFieldInputs] = useState<
    Record<string, string>
  >(() => {
    const initial: Record<string, string> = {};
    const sections = extractSections(formSections);
    sections.forEach((section) => {
      section.widgets.forEach((widget) => {
        if (
          widget.mappedApiField === "other" &&
          widget.extendedMappedApiFieldName
        ) {
          initial[widget.id] = widget.extendedMappedApiFieldName;
        }
      });
    });
    return initial;
  });

  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (meta?.api) {
      setSelectedApi(meta.api);
    } else if (location.state?.api) {
      setSelectedApi(location.state.api);
    }

    if (meta?.formName) {
      setFormName(meta.formName);
    } else if (location.state?.formName) {
      setFormName(location.state.formName);
    }

    if (meta?.buttonLabel) {
      setButtonLabel(meta.buttonLabel);
    } else if (location.state?.buttonLabel) {
      setButtonLabel(location.state.buttonLabel);
    }

    if (meta?.formId) {
      setFormId(meta.formId);
    } else if (location.state?.formId) {
      setFormId(location.state.formId);
    }
  }, [meta, location.state]);

  useEffect(() => {
    if (!hasManuallyChangedApi) return;
    setCustomMappedApiFieldInputs({});
    setJsonSchema((prevSchema) =>
      prevSchema.map((section) => ({
        ...section,
        widgets: section.widgets.map((widget) => ({
          ...widget,
          mappedApiField: undefined,
          extendedMappedApiFieldName: undefined,
        })),
      }))
    );
  }, [selectedApi, hasManuallyChangedApi]);

  useEffect(() => {
    if (formSections) {
      setJsonSchema(extractSections(formSections));
    }
  }, [formSections]);

  const handleFieldSelection = (
    widgetId: string,
    subKey: string | null,
    value: string
  ) => {
    setJsonSchema((prevSchema) => {
      const updatedSchema = prevSchema.map((section) => ({
        ...section,
        widgets: section.widgets.map((widget) => {
          if (widget.id === widgetId) {
            if (subKey) {
              const currentMappedFields = JSON.parse(widget.mappedApiField || "{}");
              const updatedMappedFields = {
                ...currentMappedFields,
                [subKey]: value,
              };
              return {
                ...widget,
                mappedApiField: JSON.stringify(updatedMappedFields),
              };
            } else {
              return {
                ...widget,
                mappedApiField: value,
              };
            }
          }
          return widget;
        }),
      }));

      if (onFormChange) {
        onFormChange(updatedSchema, [meta]);
      }

      return updatedSchema;
    });

    setUsedApiFields((prevUsedFields) => {
      const updatedFields = { ...prevUsedFields };
      if (value !== "other") {
        updatedFields[widgetId + (subKey ? `-${subKey}` : "")] = value;
      } else {
        delete updatedFields[widgetId + (subKey ? `-${subKey}` : "")];
      }
      return updatedFields;
    });
  };

  const handleCustomFieldChange = (
    widgetId: string,
    subKey: string | null,
    value: string
  ) => {
    setJsonSchema((prevSchema) => {
      const updatedSchema = prevSchema.map((section) => ({
        ...section,
        widgets: section.widgets.map((widget) => {
          if (widget.id === widgetId) {
            if (subKey) {
              const currentCustomFields = JSON.parse(
                widget.extendedMappedApiFieldName || "{}"
              );
              const updatedCustomFields = {
                ...currentCustomFields,
                [subKey]: value,
              };
              return {
                ...widget,
                extendedMappedApiFieldName: JSON.stringify(updatedCustomFields),
              };
            } else {
              return {
                ...widget,
                extendedMappedApiFieldName: value,
              };
            }
          }
          return widget;
        }),
      }));

      if (onFormChange) {
        onFormChange(updatedSchema, [meta]);
      }

      return updatedSchema;
    });
  };

  const renderDropdownWithCustomField = (
    widgetId: string,
    subKey: string | null,
    value: string,
    onChange: (value: string) => void,
    customValue: string | undefined,
    onCustomChange: (value: string) => void
  ) => {
    const isCustom = value === "other";
    const availableFields =
      selectedApi && apiConfig[selectedApi]
        ? apiConfig[selectedApi].filter(
            (field) =>
              !Object.values(usedApiFields).includes(field) ||
              usedApiFields[widgetId + (subKey ? `-${subKey}` : "")] === field
          )
        : [];

    return (
      <div style={{ marginBottom: "8px" }}>
        {subKey && <label>{subKey}:</label>}
        <select
          value={value}
          onChange={(e) => {
            const selectedValue = e.target.value;
            if (selectedValue === "other") {
              onCustomChange("");
            }
            onChange(selectedValue);
          }}
        >
          <option value="">Select Api Field</option>
          {availableFields?.map((field) => (
            <option key={field} value={field}>
              {field}
            </option>
          ))}
          <option value="other">Other</option>
        </select>
        {isCustom && (
          <input
            type="text"
            placeholder={`Enter custom ${subKey || "field"}`}
            value={customValue || ""}
            onChange={(e) => onCustomChange(e.target.value)}
            style={{ width: "75%", marginTop: "8px" }}
          />
        )}
      </div>
    );
  };

  const handleFinalize = async () => {
    if (!formName || formName === "") {
      setError("Form name is required.");
      return;
    }
    if (!buttonLabel || buttonLabel === "") {
      setError("Button label is required.");
      return;
    }
    const missingMappedApiFields = jsonSchema.some((section) =>
      section.widgets.some(
        (widget) => !widget.mappedApiField && !widget.extendedMappedApiFieldName
      )
    );

    if (missingMappedApiFields) {
      setError("All widgets must have a selected mappedApiField.");
      return;
    }

    console.log("jsonSchema", jsonSchema);
    setError("");
    const finalJsonSchema = {
      api: selectedApi,
      formName: formName,
      buttonLabel: buttonLabel,
      formId: !formId || formId === "" ? crypto.randomUUID() : formId,
      sections: jsonSchema,
      // allApiFields: selectedApi ? apiConfig[selectedApi] : [],
    };
    let formConfig: any[] = [];
    const url = "/metadata/clientconfig?name=form_configuration";
    try {
      const response = await ChannelkartNetworkGet(url);
      formConfig = response.data.features[0].domainValues;
      console.log("Form Config:", formConfig);
    } catch (err) {
      console.log(err);
    }
    const index = formConfig[0].value.findIndex(
      (form: any) => form.formId === finalJsonSchema.formId
    );
    if (index !== -1) {
      formConfig[0].value[index] = finalJsonSchema;
    } else {
      formConfig[0].value = [...formConfig[0].value, finalJsonSchema];
    }
    const finalMetaDataPayload: metaDataBatchPayload = { features: [] };
    let formPayload = {
      domainName: "clientconfig",
      domainType: "form_configuration",
      domainValues: formConfig,
      lob: getLob(),
    };
    finalMetaDataPayload.features.push(formPayload);
    if (!widgetPanelDisabled) {
      validateWithOtp(
        (verifiedOTPUser?: manageUpdateAccessObj, verifyResponse?: any) => {
          doFinalSubmit(verifiedOTPUser, verifyResponse);
        },
        undefined,
        "all",
        META_DATA_BATCH_API,
        finalMetaDataPayload,
        "PUT"
      );
    } else {
      onSave?.(
        structuredClone(finalJsonSchema.sections),
        structuredClone(finalJsonSchema)
      );
    }
  };

  const doFinalSubmit = async (
    verifiedUser?: manageUpdateAccessObj,
    verifyResponse?: any
  ) => {
    if (verifyResponse.status === 200 || verifyResponse.status === 201) {
      const validationObj = validateMetaDataResponse(
        verifyResponse?.data?.features
      );
      if (validationObj.success) {
        setPopupMessage(`done successfully`);
        setPopupType("Success");
        setOpenGenericModal(true);
        navigate("/dashboard/manageForms");
      } else {
        setPopupMessage(validationObj.message);
        setPopupType("Error");
      }
      setOpenGenericModal(true);
    } else {
      throw new Error();
    }
  };

  return (
    <div className="api-selector">
      <h2>Select API</h2>
      <select
        value={selectedApi || ""}
        onChange={(e) => {
          setHasManuallyChangedApi(true);
          setSelectedApi(e.target.value as keyof typeof apiConfig);
        }}
      >
        <option value="" disabled>
          -- Select an API --
        </option>
        {Object.keys(apiConfig).map((apiKey) => (
          <option key={apiKey} value={apiKey}>
            {apiKey}
          </option>
        ))}
      </select>

      <h2>Widgets</h2>
      {jsonSchema?.map((section) => (
        <div key={section.id} className="widget-section">
          <h3>
            {section.id} | {section.name}
          </h3>
          <div className="widget-list">
            {section.widgets.map((widget) => {
              const widgetTemplate = Widgets.find(
                (w) => w.id === widget.widgetType
              );
              if (!widgetTemplate) return null;

              return (
                <div key={widget.id} className="widget-item">
                  <div className="widget-left">
                    <div className="widget-icon">
                      {widget.icon || widgetTemplate.icon}
                    </div>
                    <div className="widget-info">
                      <p className="widget-name">
                        {widget.name || widgetTemplate.name}
                      </p>
                      <span className="widget-label">
                        Label: {widget.config.label || "Not Assigned"}
                      </span>
                    </div>
                  </div>
                  <div className="widget-right">
                    {widget.widgetType === "ranged-date" ? (
                      <>
                        {["startDate", "endDate", "difference"]
                          .filter((val) => {
                            if (val === "difference") {
                              return widget.config[val] === true;
                            }
                            return widget.config[val] !== undefined;
                          })
                          .map((subKey) =>
                            renderDropdownWithCustomField(
                              widget.id,
                              subKey,
                              subKey
                                ? JSON.parse(widget.mappedApiField || "{}")[subKey] || ""
                                : widget.mappedApiField || "",
                              (val) => handleFieldSelection(widget.id, subKey, val),
                              customMappedApiFieldInputs[
                                widget.id + (subKey ? `-${subKey}` : "")
                              ] || "",
                              (val) => handleCustomFieldChange(widget.id, subKey, val)
                            )
                          )}
                      </>
                    ) : (
                      renderDropdownWithCustomField(
                        widget.id,
                        null,
                        widget.mappedApiField || "",
                        (value) => handleFieldSelection(widget.id, null, value),
                        customMappedApiFieldInputs[widget.id],
                        (customValue) =>
                          handleCustomFieldChange(widget.id, null, customValue)
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div>
        <h2>Form Name:</h2>
        <input
          type="text"
          value={formName}
          onChange={(e) => {
            const updated = e.target.value;
            setFormName(updated);

            const updatedMeta = { ...meta, formName: updated };
            onFormChange?.(jsonSchema, [updatedMeta]);
          }}
          placeholder="Enter form name"
        />
      </div>

      <div>
        <h2>Button Label:</h2>
        <input
          type="text"
          value={buttonLabel}
          onChange={(e) => {
            const updated = e.target.value;
            setButtonLabel(updated);
            const updatedMeta = { ...meta, buttonLabel: updated };
            onFormChange?.(jsonSchema, [updatedMeta]);
          }}
          placeholder="Enter button label"
        />
      </div>

      {error && <p className="error-message">{error}</p>}

      <button className="finalize-button" onClick={handleFinalize}>
        {widgetPanelDisabled ? "Save" : "Send Form Data"}
      </button>
      <GenericPopUp
        type={popupType}
        message={popupMessage}
        setOpenGenericModal={
          popupType === "Success"
            ? () => {
                setOpenGenericModal(false);
                window.history.back();
              }
            : setOpenGenericModal
        }
        openGenericModal={openGenericModal}
      />
    </div>
  );
};

export default ApiSelector;